import React, { useEffect, useState, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  Chip,
  FormControl,
  FormLabel,
  TextField,
  Checkbox,
  Popper
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { ChevronDown, CircleX } from 'lucide-react'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { useStyles } from './style'

const ALL_OPTION = { id: 'all', name: 'Todos', isBot: false }

const CustomPopper = props => (
  <Popper
    {...props}
    placement="bottom-start"
    modifiers={{ flip: { enabled: false } }}
  />
)

export function QueueDepartmentSelect({
  value = [],
  onChange,
  label = 'Departamentos',
  placeholder = 'Selecione',
  showBots = true,
  filterByIsBot = null,
  filterNotificationAct = false,
  includeAllOption = true,
  customOptions = null,
  isMulti = true,
  disabled = false,
  maxVisibleChips = 2,
  fetchAllQueues = false,
  showLeafQueuesOnly = false,
  allowEmpty = false,
  autoSelectFirst = false
}) {
  const classes = useStyles()
  const [dataSource, setDataSource] = useState([])

  const uniqueId = useMemo(
    () => `select-${Math.random().toString(36).substring(2, 9)}`,
    []
  )

  useEffect(() => {
    if (customOptions) {
      setDataSource(customOptions)
      return
    }

    let isMounted = true
    const fetchData = async () => {
      try {
        const searchParams = new URLSearchParams()

        if (!showBots) {
          searchParams.set('excludeBot', 'true')
        }

        if (fetchAllQueues) {
          searchParams.set('includeAll', 'true')
        }

        if (showLeafQueuesOnly) {
          searchParams.set('includeOptions', 'true')
        }

        const queryString = searchParams.toString()
        const endpoint = queryString ? `/queue?${queryString}` : '/queue'
        const { data } = await api.get(endpoint)

        if (isMounted) {
          setDataSource(data)
        }
      } catch (err) {
        if (isMounted) toastError(err)
      }
    }

    fetchData()
    return () => {
      isMounted = false
    }
  }, [showBots, customOptions, fetchAllQueues, showLeafQueuesOnly])

  const filteredItems = useMemo(() => {
    return dataSource.filter(item => {
      if (showLeafQueuesOnly) {
        const hasSubDepartments =
          Array.isArray(item.options) && item.options.length > 0
        const isSubDepartment = Number(item.nivel) === 2

        if (hasSubDepartments && !isSubDepartment) return false
      }

      if (filterByIsBot === true && item.isBot !== false) return false
      if (filterByIsBot === false && item.isBot !== true) return false
      if (filterNotificationAct && item.notificationact === true) return false
      return true
    })
  }, [dataSource, filterByIsBot, filterNotificationAct, showLeafQueuesOnly])

  // Mantém o valor escolhido mesmo quando o filtro o remove das opções exibidas.
  // Isso permite que o componente pai alterne filtros sem perder a seleção existente.
  const enrichedOptions = useMemo(() => {
    const baseOptions = includeAllOption
      ? [ALL_OPTION, ...filteredItems]
      : filteredItems
    if (isMulti) return baseOptions

    const singleTarget = value[0]
    if (!singleTarget) return baseOptions

    // Garante que o valor escolhido permaneça visível mesmo quando o filtro
    // o remove das opções exibidas. Isso permite exibir corretamente o nome
    // de um departamento não-bot quando o filtro está limitado a bots.
    const rawId = String(singleTarget.id)
    const existsInDataSource = dataSource.some(
      item => String(item.id) === rawId
    )

    if (existsInDataSource) return baseOptions
    return [singleTarget, ...baseOptions]
  }, [filteredItems, includeAllOption, isMulti, value, dataSource])

  const isAllSelected = useMemo(() => {
    if (filteredItems.length === 0) return false
    const selectedRealIds = value.map(v => v.id)
    return filteredItems.every(item => selectedRealIds.includes(item.id))
  }, [filteredItems, value])

  // Pré-seleciona o primeiro item quando ainda não há valor escolhido
  useEffect(() => {
    if (!autoSelectFirst || isMulti) return
    if (value.length > 0 || filteredItems.length === 0) return
    onChange([filteredItems[0]])
  }, [autoSelectFirst, isMulti, value, filteredItems, onChange])

  // RECONCILIAÇÃO AUTOMÁTICA DE DADOS (Correção para Chips Vazios)
  // const normalizedValue = useMemo(() => {
  //   if (!isMulti) {
  //     const singleTarget = value[0] || null
  //     if (!singleTarget && filteredItems.length > 0 && !allowEmpty) {
  //       return filteredItems[0]
  //     }
  //     if (!singleTarget) return null
  //     return (
  //       filteredItems.find(
  //         item => String(item.id) === String(singleTarget.id)
  //       ) || singleTarget
  //     )
  //   }

  //   // Mapeia os IDs mockados passados pelo pai para os objetos completos com '.name' da API
  //   const fullObjects = value.map(selectedItem => {
  //     const found = filteredItems.find(
  //       item => String(item.id) === String(selectedItem.id)
  //     )
  //     return found || selectedItem
  //   })

  //   if (includeAllOption && isAllSelected && filteredItems.length > 0) {
  //     return [ALL_OPTION, ...fullObjects]
  //   }
  //   return fullObjects
  // }, [value, isMulti, includeAllOption, isAllSelected, filteredItems, allowEmpty])

  const normalizedValue = useMemo(() => {
    // Sempre resolve o nome a partir da fonte completa de dados carregada,
    // garantindo que um departamento não-bot salvo seja exibido corretamente
    // mesmo quando o filtro atual oculta departamentos.
    const resolveItem = selectedItem => {
      if (!selectedItem) return null
      const rawId = String(selectedItem.id)
      const fromDataSource = dataSource.find(
        item => String(item.id) === rawId
      )
      const fromOptions = enrichedOptions.find(
        item => String(item.id) === rawId
      )
      return fromDataSource || fromOptions || selectedItem
    }

    if (!isMulti) {
      const singleTarget = value[0] || null
      if (!singleTarget) return null
      return resolveItem(singleTarget)
    }

    const fullObjects = value.map(selectedItem => resolveItem(selectedItem))

    if (includeAllOption && isAllSelected && filteredItems.length > 0) {
      return [ALL_OPTION, ...fullObjects]
    }
    return fullObjects
  }, [value, isMulti, includeAllOption, isAllSelected, enrichedOptions, filteredItems, dataSource])

  const handleOnChange = useCallback(
    (event, selectedValues, reason, details) => {
      if (!onChange) return

      if (!isMulti) {
        // Permite a seleção de um departamento; só força o primeiro item quando
        // o usuário realmente limpa o campo (reason === "clear") e o componente
        // não permite manter o valor vazio.
        if (!selectedValues && reason === 'clear' && filteredItems.length > 0 && !allowEmpty) {
          onChange([filteredItems[0]])
          return
        }
        onChange(selectedValues ? [selectedValues] : [])
        return
      }

      const clickedAll = details?.option?.id === ALL_OPTION.id

      if (clickedAll) {
        if (isAllSelected) {
          onChange([])
        } else {
          onChange(filteredItems)
        }
      } else {
        const cleanValues = selectedValues.filter(v => v.id !== ALL_OPTION.id)
        onChange(cleanValues)
      }
    },
    [isMulti, isAllSelected, filteredItems, onChange, allowEmpty]
  )

  const getOptionLabel = useCallback(option => {
    if (option.id === ALL_OPTION.id) return option.name
    return option.name || ''
  }, [])

  return (
    <FormControl
      className={classes.formControl}
      disabled={disabled}
      component="section"
    >
      <FormLabel htmlFor={uniqueId} id={`${uniqueId}-label`}>
        {label}
      </FormLabel>
      <Autocomplete
        id={uniqueId}
        classes={{ popper: classes.popper, listbox: classes.listbox }}
        PopperComponent={CustomPopper}
        multiple={isMulti}
        disableCloseOnSelect={isMulti}
        size="small"
        disabled={disabled}
        options={enrichedOptions}
        value={normalizedValue}
        noOptionsText="Nenhuma opção disponível"
        onChange={handleOnChange}
        getOptionLabel={getOptionLabel}
        getOptionSelected={(option, val) =>
          String(option.id) === String(val?.id)
        }
        disableClearable={!isMulti && !allowEmpty && filteredItems.length > 0}
        popupIcon={<ChevronDown />}
        renderOption={(option, { selected }) => {
          const isItemChecked =
            option.id === ALL_OPTION.id ? isAllSelected : selected
          return (
            <React.Fragment>
              <Checkbox
                className={classes.checkbox}
                color="default"
                checked={isItemChecked}
                inputProps={{ 'aria-label': getOptionLabel(option) }}
              />
              {getOptionLabel(option)}
            </React.Fragment>
          )
        }}
        renderTags={(tagValue, getTagProps) => {
          const visibleData = tagValue.filter(v => v.id !== ALL_OPTION.id)
          const sliceLimit = maxVisibleChips

          const renderedChips = visibleData
            .slice(0, sliceLimit)
            .map((option, index) => (
              <Chip
                key={option.id}
                variant="outlined"
                className={classes.chip}
                style={{
                  height: '20px',
                  fontSize: '11px',
                  borderRadius: '4px'
                }}
                label={option.name || `ID: ${option.id}`} // Fallback de segurança para UX
                size="small"
                deleteIcon={<CircleX className={classes.iconButton} />}
                {...getTagProps({ index })}
              />
            ))

          if (visibleData.length > sliceLimit) {
            renderedChips.push(
              <Chip
                key="overflow-counter"
                className={classes.chipPlus}
                label={`+${visibleData.length - sliceLimit}`}
                size="small"
                style={{}}
              />
            )
          }

          return renderedChips
        }}
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            placeholder={value.length === 0 ? placeholder : ''}
            inputProps={{
              ...params.inputProps,
              'aria-labelledby': `${uniqueId}-label`
            }}
          />
        )}
      />
    </FormControl>
  )
}

QueueDepartmentSelect.propTypes = {
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  showBots: PropTypes.bool,
  filterByIsBot: PropTypes.bool,
  filterNotificationAct: PropTypes.bool,
  includeAllOption: PropTypes.bool,
  customOptions: PropTypes.array,
  isMulti: PropTypes.bool,
  disabled: PropTypes.bool,
  maxVisibleChips: PropTypes.number,
  fetchAllQueues: PropTypes.bool,
  showLeafQueuesOnly: PropTypes.bool,
  allowEmpty: PropTypes.bool,
  autoSelectFirst: PropTypes.bool
}
